import { Component, EventEmitter, Input, Output, inject } from "@angular/core";
import { DocumentModel } from "@models/documents/documentmodel";
import { DocumentManagerService } from "../../../common/services/document-manager.service";
import { DroppedDocuments, EffDropFilesAreaComponent } from "../../../common/eff-drop-files-area/eff-drop-files-area.component";
import { EffIconComponent } from "../../../common/eff-icon/eff-icon.component";
import { OpenDocumentPopupReason } from "../../../common/document-manager/models/add-documents-parameters.model";
import { PopupService } from "@app/shared/services/popup/popup.service";
import {
    EditDocumentPopup,
    OpenDocumentPopupReason as OpenReason,
} from "@app/shared/cerrix-documents/models/edit-document-popup.model";
import { MatDialog } from "@angular/material/dialog";
import { CerrixEditDocumentMonolithComponent } from "@app/shared/cerrix-documents/cerrix-edit-document/cerrix-edit-document-monolith/cerrix-edit-document-monolith.component";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { SelectOptionModel } from "@app/incident/components/src/lib/core/models/select-option.model";

@Component({
    selector: "documents-list",
    templateUrl: "./documents-list.component.html",
    styleUrls: ["./documents-list.component.scss"],
    imports: [EffIconComponent, EffDropFilesAreaComponent]
})
export class DocumentsListComponent {
    private _documentServiceManager = inject(DocumentManagerService);
    private _popupService = inject(PopupService);
    private _dialog = inject(MatDialog);
    private _sdService = inject(StandingdataDataService);

    @Input() documents: DocumentModel[];
    @Input() canAdd: boolean = true;
    @Input() canEdit: boolean = true;
    @Input() canDelete: boolean = true;
    @Output() documentsChange = new EventEmitter<DocumentModel[]>();

    @Input() taskDocuments: DocumentModel[];
    @Input() isSample: boolean = false;

    openPopupReason = OpenDocumentPopupReason;
    protected documentTypes: SelectOptionModel[] = [];
    constructor() {
        this._sdService.getDocumentTypes(null).subscribe((documentTypes) => {
            this.documentTypes = documentTypes.map((type) => {
                return { id: type.ID.toString(), name: type.Name };
            });
        });
    }

    downloadDocument(doc: DocumentModel) {
        this._documentServiceManager.downloadDocument(doc);
    }

    deleteDocument(doc: DocumentModel) {
        this._documentServiceManager.deleteDocument(doc.Guid, this.documents);
    }

    onEditDeleteDocument(doc: DocumentModel) {
        this.openEditDocumentsPopup(
            doc,
            !this.canEdit && !this.canAdd ? OpenReason.view : OpenReason.edit
        );
    }

    onEditDeleteTaskDocument(doc: DocumentModel) {
        this.openEditDocumentsPopup(doc, OpenReason.view);
    }

    onFilesDropped(droppedDocuments: DroppedDocuments) {
        if (!this.canAdd) {
            return;
        }

        if (!droppedDocuments.IsBrowse) {
            this._documentServiceManager.addEditFiles(droppedDocuments.Documents, this.documents);
            this.documentsChange.emit(this.documents);
        } else {
            this._documentServiceManager.openEditDocumentDialog(
                droppedDocuments.Documents,
                this.documents,
                OpenDocumentPopupReason.AddDocument,
                this.canAdd,
                this.canEdit,
                this.canDelete,
                (() => {
                    this.documentsChange.emit(this.documents);
                }).bind(this)
            );
        }
    }

    private openEditDocumentsPopup(document: DocumentModel, openReason: OpenReason) {
        this._documentServiceManager.convertPropertiesToAbstract(document);
        const oldDoc = document;

        const dialogInput: EditDocumentPopup = {
            documents: [document],
            documentTypes: this.documentTypes,
            openReason: openReason,
            canAddDocument: this.canAdd,
            canEditDocument: this.canEdit,
            showDelete:
                openReason === OpenReason.add || (this.canDelete && openReason != OpenReason.view),
        };

        let dialogRef = this._dialog.open(CerrixEditDocumentMonolithComponent, {
            width: "826px",
            data: dialogInput,
        });

        dialogRef.componentInstance.emitDownload.subscribe((document: DocumentModel) => {
            this.downloadDocument(document);
        });
        dialogRef.componentInstance.emitDelete.subscribe((document: DocumentModel) => {
            if (oldDoc.ID != null) {
                document.id = oldDoc.ID.toString();
            }
            this.deleteDocument(document);
        });

        dialogRef.afterClosed().subscribe((result) => {
            let doc = this.documents.find((doc) => doc.Guid === oldDoc.id);
            if (doc != null) {
                this._documentServiceManager.resetProperties(doc, oldDoc);
            }
            let taskdoc = this.taskDocuments?.find((doc) => doc.Guid === oldDoc.id);
            if (taskdoc != null) {
                this._documentServiceManager.resetProperties(taskdoc, oldDoc);
            }
            if (result?.length) {
                if (openReason === OpenReason.add) {
                    this.documents = [...this.documents, ...result];
                    this.documentsChange.emit(this.documents);
                } else if (openReason === OpenReason.edit) {
                    let parsedDocs = this.documents.map((d) => {
                        if (d.Guid === oldDoc.Guid) {
                            //undo null => "0" conversion by model conversion
                            if (result[0].documentTypeId === "0") {
                                result[0].documentTypeId = null;
                            }
                            return {
                                ...result[0],
                                Name: result[0].name,
                                Path: result[0].name,
                                Changed: true,
                                Description: result[0].description,
                                File: result[0].file,
                                CanDelete: true,
                                CanEdit: true,
                                Date: new Date(),
                                DocumentType: oldDoc.DocumentType,
                                DocumentTypeID: +result[0].documentTypeId,
                                FileIcon: result[0].fileIcon,
                                FileType: oldDoc.FileType,
                                IsNew: oldDoc.IsNew,
                                UserName: oldDoc.Username,
                                Version: d.Version++,
                            };
                        }
                        return d;
                    });
                    this.documentsChange.emit(parsedDocs);
                }
            }
        });
    }

    openDocumentViewer(document: DocumentModel) {
        const url = "/api/documents/" + document.Guid + "/pdf";
        this._popupService.forceOpenInNewWindow(url);
    }

    filteredDocuments() {
        return this.documents?.filter((doc) => !doc.Deleted);
    }

    get allowedFileExtensions() {
        return this._documentServiceManager?.documentConfig?.allowedFileExtensions;
    }
}
